import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  AiAdvisorApi,
  bulkAIAdvisorDrawerAF,
  createErrorResultFromException,
  errors,
  IAIQuestionRequestPayload,
  IAIQuestionResponse,
  IAiStreamCallbacks,
  Log,
  normalizeErrors,
  RydeUiUnit,
  SimpleResult,
  transformApiCrudErrorForToast,
  warnAndToastException,
} from "@ryde/shared";

export const useAiAdvisor = (entityTarget: RydeUiUnit) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isBulkAIAdvisorDrawerOpen, setBulkAIAdvisorDrawerOpen] =
    useRecoilState(bulkAIAdvisorDrawerAF(entityTarget));
  const abortControllerRef = useRef<AbortController | null>(null);

  const openBulkAIAdvisorDrawer = useEvent(() => {
    setBulkAIAdvisorDrawerOpen(true);
  });

  const closeBulkAIAdvisorDrawer = useEvent(() => {
    abortControllerRef.current?.abort();
    setBulkAIAdvisorDrawerOpen(false);
  });

  const question = useEvent(
    async (
      payload: IAIQuestionRequestPayload,
    ): Promise<SimpleResult<IAIQuestionResponse> | undefined> => {
      setIsLoading(true);

      try {
        const response = await AiAdvisorApi.question({ payload });

        if (response?.error) {
          const errorsNormalized = normalizeErrors(response.error, t);
          Log.warnAndToast(
            transformApiCrudErrorForToast({
              fallbackTranslationKey: errors.ai.occasionalAIResponseFailure,
              errorsNormalized,
              tFunction: t,
            }),
            response.error,
          );
        }

        return response;
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.ai.occasionalAIResponseFailure,
          translationFunction: t,
          logWarnMessage: "Error while asking AI",
        });

        return createErrorResultFromException(error, "Failed asking ai");
      } finally {
        setIsLoading(false);
      }
    },
  );

  const questionStream = useEvent(
    async (
      payload: IAIQuestionRequestPayload,
      callbacks: IAiStreamCallbacks,
    ): Promise<void> => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setIsStreaming(true);

      try {
        await AiAdvisorApi.questionStream(
          { payload },
          callbacks,
          controller.signal,
        );
      } catch (error) {
        if ((error as Error)?.name !== "AbortError") {
          warnAndToastException({
            error,
            fallbackTranslationKey: errors.ai.occasionalAIResponseFailure,
            translationFunction: t,
            logWarnMessage: "Error while streaming AI",
          });
          callbacks.onError("Stream failed");
        }
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
  );

  const cancelStream = useEvent(() => {
    abortControllerRef.current?.abort();
  });

  return {
    isBulkAIAdvisorDrawerOpen,
    openBulkAIAdvisorDrawer,
    closeBulkAIAdvisorDrawer,
    question,
    questionStream,
    cancelStream,
    isLoading,
    isStreaming,
  };
};
